import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import {
  LIVE_NOTIFICATIONS,
  NOTIFICATIONS_GROUPED_BY_DAY,
  REPLAY_NOTIFICATIONS,
  REPLAY_NOTIFICATIONS_GROUPED_BY_DAY,
  SEEN_NOTIFICATIONS,
  SEEN_REPLAY_NOTIFICATIONS,
} from '../store/notifications';
import { dayLabelTitleOrFormat } from '../util/dayLabel';
import { Notification } from '../util/Events/Messages';
import { History, PlaylistRemove } from '@mui/icons-material';
import { DisplayMessage } from './Helpers';
import { useNavigate } from 'react-router-dom';

const NotificationNode = (
  props: Notification & { map?: 'live' | 'replay'; setSeen: (id: string) => void },
) => {
  const navigate = useNavigate();

  return (
    <Tooltip title={`Click to select ${props.asset.label}`}>
      <Alert
        severity={props.replayed ? 'warning' : 'error'}
        icon={props.replayed ? <History fontSize="inherit" /> : undefined}
        variant={props.seen ? 'outlined' : 'filled'}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          const state = {
            origin: 'Notifications',
            asset: props.asset,
          };
          const navigateToTab = props.map === 'replay' ? '/replay-map' : '/live-map';
          navigate(navigateToTab, { state });
          // To stop the state from hanging around after refresh.
          setTimeout(() => {
            navigate(navigateToTab, { state: undefined, replace: true });
          });
        }}
        onClose={
          props.seen
            ? undefined
            : e => {
                props.setSeen(props.id);
                e.stopPropagation();
              }
        }
        slotProps={{
          closeButton: { title: 'Mark read' },
        }}
      >
        {format(props.date, 'h:mmaaa')}, {DisplayMessage(props)}
      </Alert>
    </Tooltip>
  );
};

export const NotificationPanel = ({ map }: { map?: 'live' | 'replay' }) => {
  const notificationsAtom = map === 'replay' ? REPLAY_NOTIFICATIONS : LIVE_NOTIFICATIONS;
  const groupedByDayAtom =
    map === 'replay' ? REPLAY_NOTIFICATIONS_GROUPED_BY_DAY : NOTIFICATIONS_GROUPED_BY_DAY;
  const notifications = useAtomValue(groupedByDayAtom);
  const liveNotifications = useAtomValue(notificationsAtom);
  const setSeen = useSetAtom(SEEN_NOTIFICATIONS);
  const setReplaySeen = useSetAtom(SEEN_REPLAY_NOTIFICATIONS);

  const setAllSeen = useCallback(() => {
    setSeen(seens => ({
      ...seens,
      ...Object.fromEntries(liveNotifications.map(({ id }) => [id, true])),
    }));
    if (map === 'replay') {
      setReplaySeen(seens => ({
        ...seens,
        ...Object.fromEntries(liveNotifications.map(({ id }) => [id, true])),
      }));
    }
  }, [liveNotifications, setSeen, setReplaySeen, map]);

  const setOneSeen = useCallback(
    (id: string) => {
      setSeen(seens => ({
        ...seens,
        [id]: true,
      }));
      if (map === 'replay') {
        setReplaySeen(seens => ({
          ...seens,
          [id]: true,
        }));
      }
    },
    [setSeen, setReplaySeen, map],
  );

  const unseenCount = notifications.flatMap(n => n).filter(n => !n.seen).length;

  return (
    <Card>
      <CardHeader title="Notifications" />
      <CardContent sx={{ maxHeight: '50vh', overflowY: 'auto', paddingTop: 0 }}>
        <Stack padding={0} spacing={2} width={400}>
          {notifications.length === 0 && (
            <Stack>
              <Typography>No notifications.</Typography>
            </Stack>
          )}
          {notifications.map(groupedNotifications => {
            const day = groupedNotifications[0].date;
            return (
              <Stack key={day.getTime()} spacing={1}>
                <Typography>{dayLabelTitleOrFormat(day, 'LLLL do, RRRR')}</Typography>
                {groupedNotifications.map(props => (
                  <NotificationNode
                    {...props}
                    key={`${props.date}${DisplayMessage(props)}`}
                    map={map}
                    setSeen={setOneSeen}
                  />
                ))}
              </Stack>
            );
          })}
        </Stack>
      </CardContent>
      {notifications.length > 0 && (
        <CardActions>
          <Button
            size="small"
            color="primary"
            endIcon={<PlaylistRemove />}
            disabled={unseenCount === 0}
            onClick={setAllSeen}
          >
            Mark all read
          </Button>
        </CardActions>
      )}
    </Card>
  );
};
